/**
 * ErrorBoundary Component
 * Catches React errors and displays a fallback UI
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import styles from './ErrorBoundary.module.css'
import { localStorageKeys } from '../../constants/storage'
import { captureException } from '../../config/sentry'

const CHUNK_RECOVERY_COUNT_KEY = localStorageKeys.chunkRecoveryCount
const MAX_CHUNK_RECOVERY_ATTEMPTS = 3;

const getChunkRecoveryCount = (): number => {
  try {
    const raw = sessionStorage.getItem(CHUNK_RECOVERY_COUNT_KEY)
    const parsed = Number(raw)
    if (!Number.isFinite(parsed) || parsed < 0) return 0
    return Math.floor(parsed)
  } catch {
    return 0
  }
}

const setChunkRecoveryCount = (count: number): void => {
  try {
    sessionStorage.setItem(CHUNK_RECOVERY_COUNT_KEY, String(Math.max(0, count)))
  } catch {
    // no-op
  }
}

function scheduleChunkRecoveryAttempt(): { scheduled: boolean; nextAttempt: number } {
  const currentAttempt = getChunkRecoveryCount()
  const nextAttempt = currentAttempt + 1

  if (currentAttempt >= MAX_CHUNK_RECOVERY_ATTEMPTS) {
    console.warn('Chunk recovery max attempts reached; showing fallback UI.')
    return { scheduled: false, nextAttempt: currentAttempt }
  }

  setChunkRecoveryCount(nextAttempt)
  const reloadDelayMs = 500 + (nextAttempt * 450)

  void (async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registrations.map((r) => r.unregister()))
      }

      if ('caches' in window) {
        const keys = await caches.keys()
        await Promise.all(keys.map((k) => caches.delete(k)))
      }
    } finally {
      setTimeout(() => {
        window.location.reload()
      }, reloadDelayMs)
    }
  })()

  return { scheduled: true, nextAttempt }
}

const clearChunkRecoveryCount = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_COUNT_KEY)
  } catch {
    // no-op
  }
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  /** When this changes while the fallback is showing, the boundary clears and renders children again (e.g. the route path, so navigating away from a crashed page works). */
  resetKey?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  suppressFallbackUi: boolean
  recoveryAttempt: number
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      suppressFallbackUi: false,
      recoveryAttempt: 0,
    }
  }

  componentDidMount(): void {
    clearChunkRecoveryCount()
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // A chunk-recovery reload is already on its way; leave its "Reconnecting…" screen up.
    if (this.state.hasError && !this.state.suppressFallbackUi && prevProps.resetKey !== this.props.resetKey) {
      this.handleReset()
    }
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Check if this is a chunk loading error
    const isChunkError = error.message.includes('error loading dynamically imported module') ||
                        error.message.includes('Loading chunk') ||
                        error.message.includes('ChunkLoadError');

    // A render crash is exactly what error tracking is for; this used to only reach the console.
    if (!isChunkError) {
      captureException(error, { componentStack: errorInfo.componentStack })
    }

    // Enhanced logging for production debugging
    console.error('=== ErrorBoundary caught an error ===')
    console.error('Error:', error)
    console.error('Error message:', error.message)
    console.error('Error stack:', error.stack)
    console.error('Component stack:', errorInfo.componentStack)
    console.error('Error name:', error.name)
    console.error('Is chunk error:', isChunkError)
    console.error('====================================')
    
    this.setState({
      error,
      errorInfo
    })

    // For chunk loading errors, automatically retry a few times before showing fallback UI
    if (isChunkError) {
      const { scheduled, nextAttempt } = scheduleChunkRecoveryAttempt()
      console.warn(`Chunk loading error detected, recovery attempt ${nextAttempt}/${MAX_CHUNK_RECOVERY_ATTEMPTS}`)
      this.setState({
        suppressFallbackUi: scheduled,
        recoveryAttempt: nextAttempt,
      })
    }

    // You can also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      suppressFallbackUi: false,
      recoveryAttempt: 0,
    })
    clearChunkRecoveryCount()
    // Optionally reload the page
    // window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.suppressFallbackUi) {
        return (
          <div className={styles.recoveringBoundary}>
            <div className={styles.recoveringContent}>
              <div className={styles.recoveringSpinner} aria-hidden="true" />
              <h2>Reconnecting…</h2>
              <p>Refreshing app resources automatically (attempt {this.state.recoveryAttempt}/{MAX_CHUNK_RECOVERY_ATTEMPTS}).</p>
            </div>
          </div>
        )
      }

      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>⚠️</div>
            <h1 className={styles.errorTitle}>Oops! Something went wrong</h1>
            <p className={styles.errorMessage}>
              We're sorry for the inconvenience. The application encountered an unexpected error.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={styles.errorDetails}>
                <summary>Error Details (Development Only)</summary>
                <div className={styles.errorStack}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className={styles.errorActions}>
              <button 
                onClick={this.handleReset} 
                className={styles.btnPrimary}
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.href = '/'} 
                className={styles.btnSecondary}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
